import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  Box,
  IconButton,
  Paper,
  Slider,
  Stack,
  Typography,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { formatTime } from '../../util/time';
import './Player.css';

interface PlayerProps {
  preSignedUrl: string | null;
  artworkPreSignedUrl?: string;
  artworkThumbnailUrl?: string | null;
  title?: string;
  artist?: string;
  release?: string;
  onPlay?: () => void;
}

const Player: React.FC<PlayerProps> = ({
  preSignedUrl,
  artworkPreSignedUrl,
  artworkThumbnailUrl,
  title,
  artist,
  release,
  onPlay,
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (preSignedUrl) {
      if (audioRef.current) {
        audioRef.current.src = preSignedUrl;
        audioRef.current.load();
      } else {
        audioRef.current = new Audio(preSignedUrl);
      }

      audioRef.current.addEventListener('loadedmetadata', () => {
        setDuration(audioRef.current?.duration || 0);
      });

      audioRef.current.addEventListener('timeupdate', () => {
        setCurrentTime(audioRef.current?.currentTime || 0);
      });

      audioRef.current.addEventListener('ended', () => {
        setIsPlaying(false);
        setCurrentTime(0);
      });
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.src = '';
      }
    };
  }, [preSignedUrl]);

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
        onPlay?.();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSliderChange = (_: Event, newValue: number | number[]) => {
    const time = newValue as number;
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  if (!preSignedUrl) {
    console.log('No preSignedUrl');
    return null;
  }

  return (
    <Paper elevation={3} className="player-paper">
      <Box className="player-container">
        <Box className="player-info">
          {(artworkThumbnailUrl ?? artworkPreSignedUrl) && (
            <img
              src={artworkThumbnailUrl ?? artworkPreSignedUrl}
              alt="Track artwork"
              className="player-artwork"
            />
          )}
          <Box className="player-track-info">
            {title && (
              <Typography variant="subtitle1" className="player-title">
                {title}
              </Typography>
            )}
            {artist && (
              <Typography variant="body2" className="player-artist">
                {artist}
              </Typography>
            )}
            {release && (
              <Typography variant="body2" className="player-release">
                {release}
              </Typography>
            )}
          </Box>
        </Box>
        <Stack
          spacing={2}
          direction="row"
          alignItems="center"
          className="player-controls"
        >
          <IconButton
            onClick={togglePlayPause}
            className="player-button"
            size="large"
          >
            {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>

          <Box className="player-slider-container">
            <Typography variant="caption" className="player-time">
              {formatTime(currentTime * 1000)}
            </Typography>
            <Slider
              value={currentTime}
              max={duration}
              onChange={handleSliderChange}
              className="player-slider"
            />
            <Typography variant="caption" className="player-time">
              {formatTime(duration * 1000)}
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Paper>
  );
};

export default Player;
